import { zodResolver } from '@hookform/resolvers/zod'
import {
  type V2CreateAdminWorkflowInput,
  V2CreateAdminWorkflowSchema
} from '@idc-forms/schemas/api-v2/admin-workflows'
import { ArrowDown, ArrowUp, X } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useStatesV2LookupQuery } from '@/features/states/hooks/use-states-v2-lookup-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'
import { CATLOG_ACTION_WORKFLOW } from '../lib/consts'

type WorkflowV2FormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<V2CreateAdminWorkflowInput>
  /** El flujo default no puede cambiar de acción (el backend responde 400). */
  lockActions?: boolean
  submitLabel: string
  pending?: boolean
  onSubmit: (values: V2CreateAdminWorkflowInput) => void
  onCancel: () => void
}

/**
 * Formulario de flujo de trabajo contra la API v2. Conserva la identidad del v1
 * —las tres secciones, el constructor de secuencia con subir/bajar/quitar y los
 * dos switches— y sólo cambia el transporte.
 *
 * NO se comparte con el v1, a diferencia del `ProfessionForm`: acá el campo
 * central diverge de verdad. El v1 bindea `{ state: <ObjectId> }[]` y alimenta su
 * selector del lookup v1 de estados; el v2 bindea un array PLANO de `sta_` —el
 * orden es la posición, no hay `order` que mandar— y consume el lookup v2
 * (`useStatesV2LookupQuery`). Mandarle un `sta_` a un endpoint v1 no daría error:
 * devolvería vacío, que es peor.
 */
export function WorkflowV2Form({
  defaultValues,
  lockActions = false,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: WorkflowV2FormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<V2CreateAdminWorkflowInput>({
    resolver: zodResolver(V2CreateAdminWorkflowSchema),
    defaultValues: {
      actions: ['new'],
      states: [],
      default: false,
      active: true,
      ...defaultValues
    }
  })

  // El servicio v2 ya desenvuelve el `{ data }` del wire: el hook devuelve el array.
  const { data: allStates = [] } = useStatesV2LookupQuery()
  // Sólo los estados de proceso entran a un flujo: los terminales (inicio, fin,
  // rechazo…) los pone el motor, no el editor.
  const processStates = allStates.filter((state) => state.type === 'process')
  const stateName = (id: string) =>
    allStates.find((state) => state.id === id)?.name ?? 'Estado eliminado'

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="workflow-name">Nombre</FieldLabel>
                <Input
                  id="workflow-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Único entre todos los flujos de trabajo.</FieldDescription>
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.description)}>
                <FieldLabel htmlFor="workflow-description">Descripción</FieldLabel>
                <Input
                  id="workflow-description"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.description]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="actions"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.actions)}>
                <FieldLabel htmlFor="workflow-action">Acción</FieldLabel>
                <NativeSelect
                  id="workflow-action"
                  disabled={lockActions}
                  value={field.value?.[0] ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value ? [e.target.value] : [])
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una acción
                  </NativeSelectOption>
                  {CATLOG_ACTION_WORKFLOW.map((action) => (
                    <NativeSelectOption key={action.value} value={action.value}>
                      {action.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {lockActions && (
                  <FieldDescription>
                    El flujo predeterminado de una acción no puede cambiar de acción.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.actions as { message?: string } | undefined]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Secuencia de estados</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="states"
            render={({ field }) => {
              // La secuencia v2 es un array plano de `sta_`: el índice ES el orden.
              const steps = field.value ?? []
              // El lookup del SDK tipa `id` como string plano; el Set se compara
              // contra esos valores, no contra la marca nominal del contrato.
              const selectedIds = new Set<string>(steps)
              const available = processStates.filter((state) => !selectedIds.has(state.id))

              const move = (index: number, delta: number) => {
                const next = [...steps]
                const target = index + delta
                if (target < 0 || target >= next.length) return
                ;[next[index], next[target]] = [next[target], next[index]]
                field.onChange(next)
              }

              return (
                <Field data-invalid={Boolean(errors.states)}>
                  <FieldLabel htmlFor="workflow-add-state">Agregar estado</FieldLabel>
                  <NativeSelect
                    id="workflow-add-state"
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return
                      field.onChange([...steps, e.target.value])
                    }}
                  >
                    <NativeSelectOption value="">
                      {available.length ? 'Selecciona un estado…' : 'Sin estados disponibles'}
                    </NativeSelectOption>
                    {available.map((state) => (
                      <NativeSelectOption key={state.id} value={state.id}>
                        {state.name}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  <FieldDescription>
                    El orden de la lista es el orden real del flujo.
                  </FieldDescription>

                  {steps.length > 0 && (
                    <ol className="mt-1 flex flex-col gap-1">
                      {steps.map((stateId, index) => (
                        <li
                          key={stateId}
                          className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1"
                        >
                          <span className="w-5 shrink-0 text-center text-xs font-medium tabular-nums text-muted-foreground">
                            {index + 1}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm">
                            {stateName(stateId)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label="Subir estado"
                            disabled={index === 0}
                            onClick={() => {
                              move(index, -1)
                            }}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label="Bajar estado"
                            disabled={index === steps.length - 1}
                            onClick={() => {
                              move(index, 1)
                            }}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label="Quitar estado"
                            onClick={() => {
                              field.onChange(steps.filter((_, i) => i !== index))
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </li>
                      ))}
                    </ol>
                  )}
                  <FieldError errors={[errors.states as { message?: string } | undefined]} />
                </Field>
              )
            }}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Estado</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="default"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="workflow-default">Predeterminado</FieldLabel>
                <Switch
                  id="workflow-default"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
          <FieldDescription>
            Solo puede haber un flujo predeterminado por acción: marcarlo desmarca el actual.
          </FieldDescription>

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="workflow-active">Activo</FieldLabel>
                <Switch
                  id="workflow-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
